import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Dialog, DialogContent, Grid, TablePagination } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Input from 'components/forms/Input'
import { searchAllCharacters } from 'utils/StringUtil'

const PAGE_SIZE = 5

// Popin des stations regroupées dans une bulle (cluster) de la carte.
// La page et la recherche sont gérées ici pour ne pas re-rendre toute la carte à chaque changement.
const ClusterStationsDialog = ({
    features = [],
    layers = [],
    getStation,
    onClose = () => {},
}) => {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    // Cartes déjà affichées : elles restent montées (masquées) pour ne pas recharger leurs données
    const [mountedIds, setMountedIds] = useState(new Set())

    useEffect(() => {
        setSearch('')
        setPage(0)
        setMountedIds(new Set())
    }, [features])

    const items = useMemo(() => features.map(feature => {
        const layer = layers.find(l => l.checkUuid?.(feature.getId()))
        const station = layer?.getUuidEntry?.(feature.getId())?.obj ?? {}
        return {
            id: feature.getId(),
            feature,
            layer,
            hash: searchAllCharacters([station.code, station.name, station.cityName, station.townCode].join(' ')),
        }
    }).filter(item => item.layer), [features, layers])

    const filteredItems = useMemo(() => {
        const searchValue = searchAllCharacters(search)
        return searchValue ? items.filter(item => item.hash.includes(searchValue)) : items
    }, [items, search])

    const nbPages = Math.ceil(filteredItems.length / PAGE_SIZE)
    const safePage = Math.min(page, Math.max(nbPages - 1, 0))
    const pageIds = useMemo(() => new Set(filteredItems.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE).map(item => item.id)), [filteredItems, safePage])

    useEffect(() => {
        setMountedIds(prev => ([...pageIds].some(id => !prev.has(id)) ? new Set([...prev, ...pageIds]) : prev))
    }, [pageIds])

    return (
        <Dialog
            onClose={onClose}
            open={features.length > 0}
            maxWidth='sm'
            fullWidth
        >
            <DefaultDialogTitle title={`${i18n.stations} (${filteredItems.length}/${items.length})`} onClose={onClose} />
            <DialogContent>
                <Grid container direction='column' spacing={1} sx={{ paddingTop: 1 }}>
                    <Grid size={12}>
                        <Input
                            title={i18n.search}
                            value={search}
                            onChange={value => {
                                setSearch(value)
                                setPage(0)
                            }}
                        />
                    </Grid>
                    {items.filter(item => mountedIds.has(item.id) || pageIds.has(item.id)).map(({ id, feature, layer }) => (
                        <Grid
                            key={id}
                            size={12}
                            sx={{
                                display: pageIds.has(id) ? 'block' : 'none',
                                '& > *': { width: '100%', maxWidth: '100%', margin: 0 },
                            }}
                        >
                            {layer.getPopup(getStation, layer, feature, true)}
                        </Grid>
                    ))}
                    {nbPages > 1 && (
                        <Grid size={12}>
                            <TablePagination
                                component='div'
                                count={filteredItems.length}
                                rowsPerPage={PAGE_SIZE}
                                rowsPerPageOptions={[]}
                                page={safePage}
                                onPageChange={(_, p) => setPage(p)}
                                showFirstButton
                                showLastButton
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

ClusterStationsDialog.propTypes = {
    features: PropTypes.arrayOf(PropTypes.shape({ getId: PropTypes.func })),
    layers: PropTypes.arrayOf(PropTypes.shape({ checkUuid: PropTypes.func })),
    getStation: PropTypes.func,
    onClose: PropTypes.func,
}

export default ClusterStationsDialog
